import {
  type PropsWithChildren,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

import type { FlightFilters } from "@/features/flights/types/types";
import { deepCompareObjects } from "@/utils/deepCompareObjects";

import {
  FlightFiltersContext,
  type FlightFiltersContextValue,
} from "./FlightFiltersContext";

type Props = PropsWithChildren<{
  defaultFilters: FlightFiltersContextValue["defaultFilters"];
}>;

export const FlightFiltersProvider = ({ defaultFilters, children }: Props) => {
  const [filters, setFilters] =
    useState<FlightFiltersContextValue["filters"]>(defaultFilters);

  // using useLayoutEffect to avoid UI flickering
  useLayoutEffect(() => {
    setFilters((prev) => {
      if (!prev) {
        return defaultFilters;
      }

      return {
        ...prev,
        ...defaultFilters,
        // retain previous selected stops if possible, all other filters depend on search result
        stops: prev.stops,
      };
    });
  }, [defaultFilters]);

  const activeFilters = useMemo(() => {
    return defaultFilters && filters
      ? deepCompareObjects<FlightFilters>(defaultFilters, filters)
      : null;
  }, [defaultFilters, filters]);

  const contextValue = useMemo<FlightFiltersContextValue>(
    () => ({
      filters,
      defaultFilters,
      activeFilters,
      updateFilters: setFilters,
    }),
    [filters, defaultFilters, activeFilters],
  );

  return (
    <FlightFiltersContext.Provider value={contextValue}>
      {children}
    </FlightFiltersContext.Provider>
  );
};
